type Consents = { lifescribe_basic: boolean; wearable_integration: boolean; ai_coaching: boolean };
type Props = {
  value: Consents;
  onChange: (c: Consents) => void;
  onNext: () => void;
  onBack: () => void;
};

const SCOPES: Array<{
  key: keyof Consents;
  title: string;
  description: string;
  required?: boolean;
}> = [
  {
    key: "lifescribe_basic",
    title: "LifeScribe™ wellness intelligence",
    description: "Allow Yvree to use your check-ins, journal entries, and session history to compute your Yvree Wellness Index and personalize your pathway.",
    required: true,
  },
  {
    key: "wearable_integration",
    title: "Wearable device integration",
    description: "Connect Whoop, Oura, Apple Health, Fitbit, Garmin, or other devices so LifeScribe can factor sleep, HRV, recovery, and activity into your score. You can disconnect any device at any time.",
  },
  {
    key: "ai_coaching",
    title: "AI-generated coaching nudges",
    description: "Receive personalized morning briefs, session recommendations, and contextual reflections generated by AI based on your wellness profile. All AI-generated content is reviewed by your human coach before becoming part of your formal LifeScribe pathway.",
  },
];

export function ConsentScreen({ value, onChange, onNext, onBack }: Props) {
  const toggle = (key: keyof Consents) => {
    if (SCOPES.find((s) => s.key === key)?.required) return;
    onChange({ ...value, [key]: !value[key] });
  };

  return (
    <div>
      <div className="text-center mb-8">
        <div className="text-[10px] tracking-[0.32em] text-teal font-medium uppercase mb-3">Privacy & consent</div>
        <h2 className="font-serif text-4xl text-textPrimary font-light leading-tight">
          Your data, <span className="italic">your choices</span>
        </h2>
        <p className="text-textBody text-sm mt-4 max-w-lg mx-auto leading-relaxed">
          You're in full control. Each option below can be toggled on or off, anytime, in your settings.
        </p>
      </div>

      <div className="space-y-3">
        {SCOPES.map((s) => {
          const enabled = value[s.key];
          return (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              disabled={s.required}
              className={`w-full text-left bg-white rounded-2xl border-2 transition p-6 ${
                enabled ? "border-teal" : "border-cardBorder"
              } ${s.required ? "cursor-default" : "hover:border-teal/40"}`}
            >
              <div className="flex items-start gap-4">
                <div className={`relative mt-1 w-10 h-6 rounded-full transition ${enabled ? "bg-teal" : "bg-sandDark"} flex-shrink-0`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${enabled ? "left-[18px]" : "left-0.5"}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <h3 className="font-medium text-textPrimary">{s.title}</h3>
                    {s.required && <span className="text-[9px] tracking-wider uppercase text-coral">Required</span>}
                  </div>
                  <p className="text-xs text-textBody leading-relaxed">{s.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-textBody mt-6 leading-relaxed text-center max-w-lg mx-auto opacity-80">
        We never sell your data, ever. Your wellness information is encrypted, access-logged,
        and accessible only to you and the practitioners you explicitly grant access to.
      </p>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="text-textBody hover:text-teal text-xs tracking-wider uppercase">
          ← Back
        </button>
        <button
          onClick={onNext}
          className="bg-teal hover:bg-tealDeep text-white tracking-wider uppercase text-xs font-medium px-10 py-3.5 rounded-lg transition"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
